import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

interface FeeRecord {
  student_id: number | string;
  student_name: string;
  class: string;
  section: string | null;
  roll_no: number;
  fee_record_id: number | string | null;
  session: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: string | number | null;
  paid_amount: string | number | null;
  fees_status: string;
  pay_mode: string | null;
}

interface DeleteFeeModalProps {
  visible: boolean;
  onClose: () => void;
  feeRecordId: number | string | null;
  feeRecord: FeeRecord | null;
  onSuccess: () => void;
  toast: React.RefObject<Toast | null>;
}

const DeleteFeeModal: React.FC<DeleteFeeModalProps> = ({
  visible,
  onClose,
  feeRecordId,
  feeRecord,
  onSuccess,
  toast,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.delete-fee-modal .p-dialog');
      const mask = document.querySelector('.delete-fee-modal');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    if (deleting) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setShowConfirmDialog(false);
    }, 400);
  };

  const handleDeleteClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!feeRecordId) return;

    setDeleting(true);
    try {
      const response = await apiClient.delete(`/api/fee-records/${feeRecordId}`);

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Fee record deleted successfully',
          life: 4000,
        });
        onSuccess();
        handleClose();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || 'Failed to delete fee record',
          life: 5000,
        });
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to delete fee record';
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: msg,
        life: 5000,
      });
    } finally {
      setDeleting(false);
      setShowConfirmDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: string | number | null) => {
    if (amount == null || amount === '') return '0';
    return Number(amount).toLocaleString('en-IN');
  };

  return (
    <>
      <style>{`
        .delete-fee-modal {
          backdrop-filter: blur(4px);
          background-color: rgba(0, 0, 0, 0.25) !important;
          animation: maskFadeIn 0.35s ease;
        }
        .delete-fee-modal.mask-closing {
          animation: maskFadeOut 0.4s ease !important;
        }
        @keyframes maskFadeIn {
          from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          to   { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
        }
        @keyframes maskFadeOut {
          from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          to   { opacity: 0; background-color: rgba(0, 0, 0, 0); }
        }
        .delete-fee-modal .p-dialog {
          animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          background: transparent !important;
          border: none !important;
        }
        .delete-fee-modal .p-dialog::before {
          content: '';
          position: absolute;
          inset: -15px;
          background: rgb(255 255 255 / 64%);
          border-radius: 40px;
          z-index: -1;
          backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }
        .delete-fee-modal .p-dialog.dialog-closing {
          animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        @keyframes dialogOpen {
          from { opacity: 0; transform: scale(0.92) translateY(-20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes dialogClose {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to   { opacity: 0; transform: scale(0.88) translateY(25px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <Dialog
        visible={visible && !isClosing}
        onHide={handleClose}
        showHeader={false}
        style={{
          width: '90vw',
          maxWidth: '520px',
          borderRadius: '32px',
          overflow: 'visible',
          boxShadow: 'none',
        }}
        contentStyle={{
          padding: '32px',
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="delete-fee-modal"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={deleting}
          style={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: deleting ? 'not-allowed' : 'pointer',
            color: '#6b7280',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s ease',
            lineHeight: 1,
            padding: 0,
            zIndex: 10,
            opacity: deleting ? 0.5 : 1,
          }}
          onMouseOver={(e) => !deleting && (e.currentTarget.style.color = '#1f2937')}
          onMouseOut={(e) => !deleting && (e.currentTarget.style.color = '#6b7280')}
        >
          ×
        </button>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
          paddingBottom: '20px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <div style={{
            width: '54px',
            height: '54px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
            border: '3px solid white',
          }}>
            <i className="pi pi-exclamation-triangle" style={{ color: 'white', fontSize: '24px' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
              Delete Fee Record
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
              Review details before deletion
            </div>
          </div>
        </div>

        {/* Content */}
        <div>
          {feeRecord && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                background: '#fef2f2',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #fecaca',
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#991b1b',
                  marginBottom: '14px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid #fecaca',
                }}>
                  Fee Record Details
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    { label: 'Student Name', value: feeRecord.student_name },
                    { label: 'Class', value: `${feeRecord.class}${feeRecord.section ? ` - ${feeRecord.section}` : ''}` },
                    { label: 'Roll No', value: String(feeRecord.roll_no) },
                    { label: 'Receipt No', value: feeRecord.receipt_no ?? 'N/A' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '600' }}>{label}:</span>
                      <span style={{ fontSize: '13px', color: '#7f1d1d', fontWeight: '500' }}>{value}</span>
                    </div>
                  ))}

                  {feeRecord.fees_status.toLowerCase() === 'paid' && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '600' }}>Paid Amount:</span>
                        <span style={{ fontSize: '13px', color: '#7f1d1d', fontWeight: '500' }}>
                          ₹{formatAmount(feeRecord.paid_amount)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '600' }}>Paid Date:</span>
                        <span style={{ fontSize: '13px', color: '#7f1d1d', fontWeight: '500' }}>
                          {formatDate(feeRecord.paid_date)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '600' }}>Payment Mode:</span>
                        <span style={{ fontSize: '13px', color: '#7f1d1d', fontWeight: '500' }}>
                          {feeRecord.pay_mode?.toUpperCase() ?? 'N/A'}
                        </span>
                      </div>
                    </>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '600' }}>Status:</span>
                    <span style={{ fontSize: '13px', color: '#7f1d1d', fontWeight: '600', textTransform: 'uppercase' }}>
                      {feeRecord.fees_status}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                background: '#fff7ed',
                borderRadius: '8px',
                border: '1px solid #fed7aa',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                <i className="pi pi-info-circle" style={{ color: '#c2410c', fontSize: '16px' }} />
                <p style={{ margin: 0, fontSize: '12px', color: '#9a3412', fontWeight: '500', lineHeight: 1.5 }}>
                  This action cannot be undone. The fee record will be permanently removed from the system.
                </p>
              </div>

              <div style={{ marginTop: '24px' }}>
                <GlobalButton
                  label={deleting ? 'Deleting...' : 'Delete Fee Record'}
                  onClick={handleDeleteClick}
                  variant="danger"
                  fullWidth
                  icon="pi pi-trash"
                  disabled={deleting}
                />
              </div>
            </div>
          )}
        </div>

        {/* Mini Confirmation Dialog */}
        {showConfirmDialog && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            animation: 'fadeIn 0.2s ease',
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              maxWidth: '320px',
              width: '90%',
              animation: 'scaleIn 0.2s ease',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                inset: '-12px',
                background: 'rgb(255 255 255 / 48%)',
                borderRadius: '24px',
                zIndex: -1,
                backdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }} />

              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
                  Confirm Deletion
                </h3>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                  Are you absolutely sure?
                </p>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <GlobalButton
                  label={deleting ? 'Deleting...' : 'Delete'}
                  onClick={handleConfirmDelete}
                  variant="danger"
                  size="small"
                  fullWidth
                  disabled={deleting}
                />
                <GlobalButton
                  label="Cancel"
                  onClick={handleCancelDelete}
                  variant="outline"
                  size="small"
                  fullWidth
                  disabled={deleting}
                />
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default DeleteFeeModal;